import {focusFirstErrorField, useAppForm} from '@cypro/ui/integrations/form'
import {isDefined, removeElementAt} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {type SubmitEvent, useCallback} from 'react'
import {MedicationProfileFormOptions} from '../constants'
import {
  closeMedicationProfileFormDialog,
  openMedicationProfileFormDialog,
} from '../medication-profiles/components/medication-profile-form/util'
import {getMedicationProfileFormDefaultValues} from '../medication-profiles/functions'
import {MedicationProfileFormSchema} from '../medication-profiles/schemas'
import {useStabilityRuleForm} from './use-stability-rule-form'

/**
 * Called when the medication profile dialog is submitted with a valid profile. The dialog is reset and closed for you once the handler resolves, so the handler only has to decide where the profile goes.
 */
export type MedicationProfileSubmitHandler = (
  value: MedicationProfileFormSchema,
  meta: {index: number}
) => void | Promise<void>

interface UseMedicationProfileFormOptions {
  onSubmit: MedicationProfileSubmitHandler
}

/**
 * Builds the medication-profile form and its nested stability-rule form, plus the handlers needed to open the dialog. Callers own the destination of the submitted profile via `onSubmit` (e.g. append to a drug form's `medicationProfiles`, or persist it to an existing drug).
 */
export function useMedicationProfileForm({onSubmit}: UseMedicationProfileFormOptions) {
  const {t} = useTranslate('common')

  const medicationProfileForm = useAppForm({
    ...MedicationProfileFormOptions,
    async onSubmit({value, formApi, meta}) {
      await onSubmit(value, meta)
      formApi.reset()
      closeMedicationProfileFormDialog()
    },
    onSubmitInvalid() {
      focusFirstErrorField()
    },
  })

  const {onAddStabilityRule, onEditStabilityRule} = useStabilityRuleForm({
    getStabilityRules: () => medicationProfileForm.state.values.stabilityRules,
    setStabilityRules: rules => medicationProfileForm.setFieldValue('stabilityRules', rules),
  })

  const onDeleteStabilityRule = useCallback(
    (index: number) => {
      medicationProfileForm.setFieldValue(
        'stabilityRules',
        removeElementAt(medicationProfileForm.state.values.stabilityRules, index)
      )
    },
    [medicationProfileForm]
  )

  const onAddMedicationProfile = useCallback(
    async (index?: number) => {
      function submit(event: SubmitEvent) {
        event.preventDefault()
        medicationProfileForm.handleSubmit({index: isDefined(index) ? index : -1})
      }

      await openMedicationProfileFormDialog({
        title: t('add_medicine_form_dialog.title'),
        description: t('add_medicine_form_dialog.description'),
        form: medicationProfileForm,
        onSubmit: submit,
        onAddStabilityRule,
        onEditStabilityRule,
        onDeleteStabilityRule,
      })

      medicationProfileForm.reset(getMedicationProfileFormDefaultValues(undefined))
    },
    [t, medicationProfileForm, onAddStabilityRule, onEditStabilityRule, onDeleteStabilityRule]
  )

  const onEditMedicationProfile = useCallback(
    (profile: MedicationProfileFormSchema, index: number) => {
      medicationProfileForm.reset(profile)
      onAddMedicationProfile(index)
    },
    [medicationProfileForm, onAddMedicationProfile]
  )

  return {medicationProfileForm, onAddMedicationProfile, onEditMedicationProfile}
}
